import { useState } from "react";
import { useParams } from "react-router-dom";
import { PatientRehabProtocolDto, patientRehabProtocolFormData } from "../types/patientRehabProtocol";
import { patientRehabProtocolService } from "../services/patientRehabProtocolService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const usePatientRehabProtocols = (overridePatientId?: string) => {
  const { patientId: urlPatientId } = useParams<{ patientId: string }>();
  const patientId = overridePatientId ?? urlPatientId;

  // ΕΝΑ ΚΑΙ ΜΟΝΑΔΙΚΟ request: Επιλέγει αυτόματα endpoint ανάλογα με το αν υπάρχει patientId
  const {
    data: rehabProtocols = [],
    loading: isFetching,
    error: getError,
    refetch,
    invalidateCache,
  } = useApi<PatientRehabProtocolDto[]>(
    patientId ? `patient-rehab-protocols-${patientId}` : "patient-rehab-protocols-all",
    async (signal) => {
      if (patientId) {
        return patientRehabProtocolService.getByPatientId(patientId, signal);
      }
      return patientRehabProtocolService.getAll({}, signal);
    },
    [patientId]
  );

  // Mutation για Create (προσθέτει αυτόματα το patientId από το URL στο payload)
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newProtocol:typeof patientRehabProtocolFormData) => {
    const payload = patientId 
      ? { ...newProtocol, patientId: Number(patientId) } 
      : newProtocol;
      
    return patientRehabProtocolService.create(payload as any);
  });
const {
  execute: updateExecution,
  loading: isUpdating,
  error: updateError,
} = useMutation(({ id, data }: { id: number | string; data: PatientRehabProtocolDto }) =>
  patientRehabProtocolService.update(id, data)
);
  // Mutation για Delete
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => patientRehabProtocolService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createPatientRehabProtocol = async (newProtocol: typeof patientRehabProtocolFormData): Promise<void> => {
    try {
      await createExecution(newProtocol);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το πρωτόκολλο αποκατάστασης αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };
const updatePatientRehabProtocol = async (id: number | string, updatedData: PatientRehabProtocolDto): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };
  const deletePatientRehabProtocols = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    patientRehabProtocols: rehabProtocols, // Τα δεδομένα (είτε για έναν ασθενή, είτε όλα)
    allRehabProtocols: rehabProtocols,     // Alias για συμβατότητα
    isLoading: isFetching || isCreating || isDeleting,
    error: getError || createError || deleteError,
    fetchPatientRehabProtocols: () => refetch(true),
    fetchRehabProtocols: () => refetch(true),
    createPatientRehabProtocol,
    updatePatientRehabProtocol,
    deletePatientRehabProtocols,
    modalState,
    closeModal,
  };
};